import { useCallback, useEffect, useRef, useState } from "react";
import { avmApi } from "./api";
import { connectStorageScope } from "../../base_path";
import { announceSessionChange } from "../../session_events";

// Orchestrates the Home Value AVM flow as a small state machine (SUR-1866):
//
//   search → loading (create + poll while ez "processing")
//          → locked   (ez ready, but visitor NOT registered — server withheld figures)
//          → revealed (ez ready AND registered — figures present)
//          → needs_setup | error  (graceful dead-ends)
//
// The figures only ever arrive from the server when the visitor is registered,
// so "locked" surfaces the registration gate; once the code is verified the
// session cookie exists and a re-poll returns the figures.
const AUTOCOMPLETE_DEBOUNCE_MS = 300;
const POLL_BASE_MS = 2000;
const POLL_MAX_MS = 5000;
const POLL_MAX_ATTEMPTS = 30;

// Persist the in-flight valuation across a FULL-PAGE return from the email
// magic link (/home-value?registered=1): the figures are session-owned server
// side, but the in-memory user_home_id is lost on reload, so the visitor would
// otherwise land back on a blank search. Use localStorage (NOT sessionStorage)
// because tapping an email link usually opens a NEW TAB, and sessionStorage is
// per-tab — localStorage is shared across same-origin tabs. Host-scoped so it
// can't bleed across agents, stamped with a timestamp + short TTL (localStorage
// persists across sessions, so a long-abandoned entry must not auto-resume),
// and cleared once the figures are revealed.
const RESUME_TTL_MS = 30 * 60 * 1000; // covers the 15-min magic-link window + slack
function resumeKey() {
  const scope = typeof window !== "undefined" ? connectStorageScope() : "";
  return `connect_avm_pending:${scope}`;
}
function savePending(data) {
  try {
    window.localStorage.setItem(resumeKey(), JSON.stringify({ ...data, ts: Date.now() }));
  } catch (_e) {
    /* storage unavailable (private mode / SSR) — resume is best-effort */
  }
}
function loadPending() {
  try {
    const raw = window.localStorage.getItem(resumeKey());
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data.ts !== "number" || Date.now() - data.ts > RESUME_TTL_MS) {
      clearPending(); // stale/abandoned — don't resurrect an old valuation
      return null;
    }
    return data;
  } catch (_e) {
    return null;
  }
}
function clearPending() {
  try {
    window.localStorage.removeItem(resumeKey());
  } catch (_e) {
    /* no-op */
  }
}

// `monthlyUpdatesConsent`: whether the address form is showing the monthly-email
// consent line. Captured per valuation when the visitor picks an address and
// sent with attribution, so the server enrolls only a visitor who actually saw
// it — not one who requested under "off" and finished registering after the
// agent switched auto-enrollment on.
export function useAvmFlow({ templateKey, disabled = false, monthlyUpdatesConsent = false } = {}) {
  const [phase, setPhase] = useState("search");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  // Address lookup failed upstream (as opposed to legitimately matching nothing).
  const [lookupError, setLookupError] = useState("");
  const [valuation, setValuation] = useState(null);
  const [property, setProperty] = useState(null);
  const [comps, setComps] = useState([]);
  const [deeds, setDeeds] = useState([]);
  const [mortgages, setMortgages] = useState([]);
  const [agentEstimate, setAgentEstimate] = useState(null);
  const [regError, setRegError] = useState(null);
  const [leadDone, setLeadDone] = useState(false);
  const [leadError, setLeadError] = useState(null);
  const [saved, setSaved] = useState([]);

  const userHomeId = useRef(null);
  const valuedAddress = useRef("");
  const consentShown = useRef(false);
  const pollTimer = useRef(null);
  const debounceTimer = useRef(null);
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
    clearTimeout(pollTimer.current);
    clearTimeout(debounceTimer.current);
  }, []);

  // --- Address autocomplete (debounced) -----------------------------------
  // `searching` drives the immediate "Searching…" dropdown state: it flips true
  // the instant a query is long enough to fetch (before the debounce fires) and
  // back to false when results/needs_setup/error settle. Below the 3-char API
  // minimum it stays false so the UI can show a "keep typing" hint instead.
  const onQueryChange = useCallback((value) => {
    setQuery(value);
    clearTimeout(debounceTimer.current);
    const trimmed = value.trim();
    if (trimmed.length < 3) {
      setSuggestions([]);
      setLookupError("");
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceTimer.current = setTimeout(async () => {
      try {
        const { needsSetup, suggestions: list, failed } = await avmApi.autocomplete(trimmed);
        if (!mounted.current) return;
        if (needsSetup) {
          setPhase("needs_setup");
          return;
        }
        // An ez outage previously arrived here as an empty list, so the visitor
        // saw "no matches" for a valid address and had no way to start a
        // valuation. Say it's us, and that retrying is worth it.
        setLookupError(failed ? "We couldn't load address suggestions just now. Please try again." : "");
        setSuggestions(list);
      } catch (_e) {
        // Network/fetch failure — drop suggestions, keep the search usable.
        if (mounted.current) {
          setSuggestions([]);
          setLookupError("We couldn't load address suggestions just now. Please try again.");
        }
      } finally {
        if (mounted.current) setSearching(false);
      }
    }, AUTOCOMPLETE_DEBOUNCE_MS);
  }, []);

  // --- Polling loop --------------------------------------------------------

  // The AGENT's layer on a report — their estimate, and the SUR-2680 property-spec
  // corrections merged over ez's assessor record — is only serialized once the ez
  // user_home is attached to the visitor's contact, which is what the attribution
  // POST below does. On a FIRST reveal the poll that produced the figures ran
  // before that, so an agent-prepared report paints ez's raw specs and no agent
  // estimate, then silently changes on the visitor's next visit. Re-poll once
  // attribution lands and fold in just those two fields: the reveal is never
  // delayed, everything else in the response is already correct, and the report is
  // cached server-side for CACHE_TTL so this costs no extra ez call.
  const syncAgentLayer = useCallback((id) => {
    avmApi
      .poll(id)
      .then((res) => {
        // Drop the response if the visitor opened a different saved report (or
        // left) while it was in flight — a stale merge would overwrite the one
        // they are actually looking at.
        if (!mounted.current || userHomeId.current !== id || res.state !== "ready") return;
        setProperty(res.property || null);
        setAgentEstimate(res.agentEstimate || null);
      })
      .catch(() => {
        /* best-effort — the revealed report is complete without the agent layer */
      });
  }, []);

  const finishReveal = useCallback(
    (res) => {
      const id = userHomeId.current;
      clearPending(); // figures revealed — no resume needed
      setValuation(res.valuation || null);
      setProperty(res.property || null);
      setComps(res.comps || []);
      setDeeds(res.deeds || []);
      setMortgages(res.mortgages || []);
      setAgentEstimate(res.agentEstimate || null);
      setPhase("revealed");
      // AVM attribution (registered-only server-side) + AI-chat hook (Phase 3).
      avmApi
        .recordAttribution(id, {
          valuedAddress: valuedAddress.current,
          templateKey,
          estimate: res.valuation?.estimate,
          monthlyUpdatesConsent: consentShown.current,
        })
        .then(() => syncAgentLayer(id))
        .catch(() => {
          /* attribution is best-effort server-side too — nothing to surface */
        });
      try {
        window.dispatchEvent(
          new CustomEvent("connect:avm-result", {
            detail: { address: valuedAddress.current, valuation: res.valuation, comps: res.comps },
          })
        );
      } catch (_e) {
        /* CustomEvent unsupported — non-fatal */
      }
    },
    [templateKey, syncAgentLayer]
  );

  const poll = useCallback(
    (attempt = 0) => {
      clearTimeout(pollTimer.current);
      avmApi.poll(userHomeId.current).then((res) => {
        if (!mounted.current) return;
        if (res.state === "processing") {
          if (attempt >= POLL_MAX_ATTEMPTS) {
            setPhase("error");
            return;
          }
          const delay = Math.min(POLL_BASE_MS + attempt * 500, POLL_MAX_MS);
          pollTimer.current = setTimeout(() => poll(attempt + 1), delay);
        } else if (res.state === "locked") {
          // Remember the valuation so a magic-link return can resume it.
          savePending({
            userHomeId: userHomeId.current,
            valuedAddress: valuedAddress.current,
            monthlyUpdatesConsent: consentShown.current,
          });
          setPhase("locked");
        } else if (res.state === "ready") {
          finishReveal(res);
        } else if (res.state === "needs_setup") {
          setPhase("needs_setup");
        } else {
          setPhase("error");
        }
      }).catch(() => {
        // Network/fetch failure mid-poll — surface the error state, don't hang.
        if (mounted.current) setPhase("error");
      });
    },
    [finishReveal]
  );

  // --- Deep-link into a specific report / resume after a magic-link return --
  // On mount, honour a /home-value?avm=<user_home_id> deep link first — this is
  // how the account dashboard's compact "View full report" link opens the exact
  // valuation the visitor just ran. Falls back to the localStorage resume: if a
  // prior valuation is pending (saved when the gate was shown), restore its
  // session-owned id and re-poll. If the visitor has now registered (e.g. just
  // tapped the email link) the figures unlock; if not, the poll just re-shows
  // the gate with the address preserved.
  useEffect(() => {
    if (disabled) return;
    let deepLinkId = null;
    try {
      deepLinkId = new URLSearchParams(window.location.search).get("avm");
    } catch (_e) {
      /* URLSearchParams unavailable (SSR) — deep link is best-effort */
    }
    if (deepLinkId) {
      userHomeId.current = deepLinkId;
      consentShown.current = false; // a re-opened report, not a fresh request
      setPhase("loading");
      poll(0);
      return;
    }
    const pending = loadPending();
    if (!pending || !pending.userHomeId) return;
    userHomeId.current = pending.userHomeId;
    valuedAddress.current = pending.valuedAddress || "";
    consentShown.current = pending.monthlyUpdatesConsent === true;
    setPhase("loading");
    poll(0);
  }, [disabled]);

  // --- Start a valuation for the selected address --------------------------
  const selectAddress = useCallback(
    async (suggestion) => {
      valuedAddress.current = suggestion.label;
      consentShown.current = monthlyUpdatesConsent;
      setQuery(suggestion.label);
      setSuggestions([]);
      setPhase("loading");
      try {
        const { needsSetup, userHomeId: id } = await avmApi.create(suggestion.c_id);
        if (!mounted.current) return;
        if (needsSetup) {
          setPhase("needs_setup");
          return;
        }
        if (!id) {
          setPhase("error");
          return;
        }
        userHomeId.current = id;
        poll(0);
      } catch (_e) {
        // Network/fetch failure kicking off the valuation — don't hang on loading.
        if (mounted.current) setPhase("error");
      }
    },
    [poll, monthlyUpdatesConsent]
  );

  // --- Saved home values (returning, signed-in visitor) --------------------
  // Load the visitor's saved valuations on mount (empty for an unregistered
  // visitor). Re-open one by re-polling its ez user_home_id — same path as the
  // magic-link resume — which the server authorizes for the owning person.
  const refreshSaved = useCallback(async () => {
    try {
      const list = await avmApi.listSaved();
      if (mounted.current) setSaved(Array.isArray(list) ? list : []);
    } catch (_e) {
      /* best-effort — the search still works without the saved list */
    }
  }, []);

  useEffect(() => {
    if (disabled) return;
    refreshSaved();
  }, [disabled, refreshSaved]);

  const openSaved = useCallback(
    (homeValue) => {
      if (!homeValue?.user_home_id) return;
      userHomeId.current = homeValue.user_home_id;
      valuedAddress.current = homeValue.address || "";
      consentShown.current = false;
      setPhase("loading");
      poll(0);
    },
    [poll]
  );

  const removeSaved = useCallback(async (id) => {
    setSaved((list) => list.filter((hv) => hv.id !== id)); // optimistic
    try {
      await avmApi.deleteSaved(id);
    } catch (_e) {
      refreshSaved(); // restore truth on failure
    }
  }, [refreshSaved]);

  // --- Registration gate ---------------------------------------------------
  // Step 1: send the code/link. Step 2: verify the typed code, which establishes
  // the Connect session; then re-poll to receive the now-unlocked figures.
  const requestCode = useCallback(async (contact) => {
    setRegError(null);
    // Mirror the server's minimum (10 digits with area code, as in
    // lead_capture_controller) so a typo'd phone gets a field-specific message
    // instead of a round-trip and a generic failure.
    if (contact.phone && contact.phone.replace(/\D/g, "").length < 10) {
      setRegError("Please enter a valid mobile phone number with area code.");
      return false;
    }
    try {
      const { ok, status, error } = await avmApi.requestCode(contact);
      if (!mounted.current) return false;
      if (!ok) {
        // Prefer the server's field-accurate message (bad phone vs bad email vs
        // failed SMS send) over a generic one that blames the wrong field. The
        // 429 body carries the machine token "rate_limited", not user copy.
        setRegError(
          status === 429
            ? "Too many requests right now. Please wait a minute and try again."
            : (error && error !== "rate_limited" && error) ||
                "We couldn't send your code. Double-check your details and try again."
        );
        return false;
      }
      return true;
    } catch (_e) {
      if (mounted.current) setRegError("We couldn't send your code. Please try again.");
      return false;
    }
  }, []);

  const verifyCode = useCallback(
    async (contact) => {
      setRegError(null);
      try {
        const result = await avmApi.verifyCode(contact);
        if (!mounted.current) return false;
        if (!result.ok) {
          setRegError("That code didn't match. Check it and try again.");
          return false;
        }
        // Half done: the text proved the handset, an emailed code still has to
        // prove the account. Nobody is signed in yet, so don't start polling.
        if (result.needsEmailConfirmation) return { needsEmailConfirmation: true, sentTo: result.sentTo };
        // The session now exists but nothing navigated, so the rest of the page
        // (header auth widget, favorite hearts) is still painted signed-out.
        announceSessionChange({ source: "avm" });
        // Same staleness inside the widget: the saved list was fetched on mount,
        // when the server had no session to answer for and returned nothing.
        refreshSaved();
        setPhase("loading");
        poll(0);
        return "complete";
      } catch (_e) {
        if (mounted.current) setRegError("Something went wrong verifying your code. Please try again.");
        return false;
      }
    },
    [poll, refreshSaved]
  );

  // --- needs_setup lead fallback -------------------------------------------
  // When the AVM isn't available (tenant not provisioned), still capture the
  // visitor into the agent's CRM via /_lead — the role the removed
  // {% lead_form %} used to play on this page.
  const captureLead = useCallback(
    async ({ name, email, phone }) => {
      setLeadError(null);
      try {
        const { ok } = await avmApi.submitLead({ name, email, phone, templateKey });
        if (!mounted.current) return false;
        if (!ok) {
          setLeadError("We couldn't save your details. Please try again.");
          return false;
        }
        setLeadDone(true);
        return true;
      } catch (_e) {
        if (mounted.current) setLeadError("We couldn't save your details. Please try again.");
        return false;
      }
    },
    [templateKey]
  );

  return {
    phase,
    query,
    suggestions,
    searching,
    lookupError,
    // The session-owned ez user_home id backing the revealed report — exposed so
    // the compact account card can deep-link "View full report" straight to it.
    userHomeId: userHomeId.current,
    valuation,
    property,
    comps,
    deeds,
    mortgages,
    agentEstimate,
    saved,
    openSaved,
    removeSaved,
    regError,
    leadDone,
    leadError,
    onQueryChange,
    selectAddress,
    requestCode,
    verifyCode,
    captureLead,
  };
}
